import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faSearch, faUser } from "@fortawesome/free-solid-svg-icons";
import { supabase } from "../lib/supabase";
import "../styles/followListOverlay.css";

const PAGE_SIZE = 10;

export default function FollowListOverlay({ open, onClose, profileId, mode }) {
    // mode: "followers" | "following"
    const navigate = useNavigate();

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [searchResults, setSearchResults] = useState(null); // null = not searching
    const [searchLoading, setSearchLoading] = useState(false);

    const offsetRef = useRef(0);
    const isFetchingRef = useRef(false);
    const hasMoreRef = useRef(true);
    const listRef = useRef(null);
    const searchTimerRef = useRef(null);

    // ── FETCH PAGE ─────────────────────────────────────────────────────────────
    const fetchPage = useCallback(async (isInitial = false) => {
        if (!profileId) return;
        if (isFetchingRef.current) return;
        if (!hasMoreRef.current && !isInitial) return;

        isFetchingRef.current = true;
        if (isInitial) setLoading(true);
        else setLoadingMore(true);

        const from = offsetRef.current;
        const to = from + PAGE_SIZE - 1;

        let userIds = [];

        if (mode === "followers") {
            // people who follow profileId
            const { data } = await supabase
                .from("follows")
                .select("follower_id")
                .eq("following_id", profileId)
                .range(from, to)
                .order("created_at", { ascending: false });
            userIds = (data || []).map(r => r.follower_id);
        } else {
            // people profileId follows
            const { data } = await supabase
                .from("follows")
                .select("following_id")
                .eq("follower_id", profileId)
                .range(from, to)
                .order("created_at", { ascending: false });
            userIds = (data || []).map(r => r.following_id);
        }

        if (userIds.length < PAGE_SIZE) {
            hasMoreRef.current = false;
            setHasMore(false);
        }

        let profiles = [];
        if (userIds.length > 0) {
            const { data: profilesData } = await supabase
                .from("profiles")
                .select("id, username, full_name, avatar_url")
                .in("id", userIds);
            // preserve order from follows query
            const map = {};
            (profilesData || []).forEach(p => { map[p.id] = p; });
            profiles = userIds.map(id => map[id]).filter(Boolean);
        }

        offsetRef.current = from + userIds.length;
        setItems(prev => isInitial ? profiles : [...prev, ...profiles]);

        setLoading(false);
        setLoadingMore(false);
        isFetchingRef.current = false;
    }, [profileId, mode]);

    // Reset + fetch when opened
    useEffect(() => {
        if (!open || !profileId) return;
        offsetRef.current = 0;
        hasMoreRef.current = true;
        setHasMore(true);
        setItems([]);
        setSearchQuery("");
        setSearchResults(null);
        fetchPage(true);
    }, [open, profileId, mode]);

    // ── SCROLL on list ─────────────────────────────────────────────────────────
    useEffect(() => {
        const el = listRef.current;
        if (!el) return;
        const handleScroll = () => {
            if (searchResults !== null) return; // don't paginate during search
            if (isFetchingRef.current || !hasMoreRef.current) return;
            if (el.scrollHeight - el.scrollTop - el.clientHeight < 150) {
                fetchPage(false);
            }
        };
        el.addEventListener("scroll", handleScroll, { passive: true });
        return () => el.removeEventListener("scroll", handleScroll);
    }, [fetchPage, searchResults]);

    // ── SEARCH (debounced) ─────────────────────────────────────────────────────
    useEffect(() => {
        clearTimeout(searchTimerRef.current);

        if (!searchQuery.trim()) {
            setSearchResults(null);
            return;
        }

        searchTimerRef.current = setTimeout(async () => {
            setSearchLoading(true);
            const q = searchQuery.trim().toLowerCase();

            // Get all follow relationship IDs first (no pagination for search)
            let allUserIds = [];
            if (mode === "followers") {
                const { data } = await supabase
                    .from("follows")
                    .select("follower_id")
                    .eq("following_id", profileId);
                allUserIds = (data || []).map(r => r.follower_id);
            } else {
                const { data } = await supabase
                    .from("follows")
                    .select("following_id")
                    .eq("follower_id", profileId);
                allUserIds = (data || []).map(r => r.following_id);
            }

            if (allUserIds.length === 0) {
                setSearchResults([]);
                setSearchLoading(false);
                return;
            }

            // Search by username OR full_name within the follow set
            const { data: profilesData } = await supabase
                .from("profiles")
                .select("id, username, full_name, avatar_url")
                .in("id", allUserIds)
                .or(`username.ilike.%${q}%,full_name.ilike.%${q}%`)
                .limit(10);

            setSearchResults(profilesData || []);
            setSearchLoading(false);
        }, 300);

        return () => clearTimeout(searchTimerRef.current);
    }, [searchQuery, profileId, mode]);

    if (!open) return null;

    const displayList = searchResults !== null ? searchResults : items;
    const title = mode === "followers" ? "Followers" : "Following";

    const handleUserClick = (username) => {
        onClose();
        navigate(`/user/${encodeURIComponent(username)}`);
    };

    return (
        <div className="flo-backdrop" onClick={onClose}>
            <div className="flo-sheet" onClick={e => e.stopPropagation()}>

                {/* HEADER */}
                <div className="flo-header">
                    <span className="flo-title">{title}</span>
                    <button className="flo-close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                <div className="flo-divider" />

                {/* SEARCH */}
                <div className="flo-search-wrap">
                    <FontAwesomeIcon icon={faSearch} className="flo-search-icon" />
                    <input
                        className="flo-search-input"
                        placeholder="Search"
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        autoComplete="off"
                    />
                </div>

                {/* LIST */}
                <div className="flo-list" ref={listRef}>

                    {/* Initial loading skeletons */}
                    {loading && (
                        <>
                            {[1,2,3,4,5].map(i => (
                                <div key={i} className="flo-skeleton-row">
                                    <div className="flo-skel-avatar" />
                                    <div className="flo-skel-lines">
                                        <div className="flo-skel-name" />
                                        <div className="flo-skel-sub" />
                                    </div>
                                </div>
                            ))}
                        </>
                    )}

                    {/* Search loading */}
                    {!loading && searchLoading && (
                        <div className="flo-center-msg">
                            <div className="flo-spinner" />
                        </div>
                    )}

                    {/* Empty states */}
                    {!loading && !searchLoading && displayList.length === 0 && searchResults !== null && (
                        <div className="flo-center-msg flo-empty">No results for "{searchQuery}"</div>
                    )}
                    {!loading && !searchLoading && displayList.length === 0 && searchResults === null && !hasMore && (
                        <div className="flo-center-msg flo-empty">
                            {mode === "followers" ? "No followers yet." : "Not following anyone yet."}
                        </div>
                    )}

                    {/* User rows */}
                    {!loading && !searchLoading && displayList.map(person => (
                        <div
                            key={person.id}
                            className="flo-user-row"
                            onClick={() => handleUserClick(person.username)}
                        >
                            {person.avatar_url ? (
                                <img src={person.avatar_url} className="flo-avatar" alt="" />
                            ) : (
                                <div className="flo-avatar-fallback">
                                    <FontAwesomeIcon icon={faUser} />
                                </div>
                            )}
                            <div className="flo-user-info">
                                <span className="flo-username">{person.username}</span>
                                {person.full_name && (
                                    <span className="flo-fullname">{person.full_name}</span>
                                )}
                            </div>
                        </div>
                    ))}

                    {/* Load-more spinner */}
                    {loadingMore && (
                        <div className="flo-center-msg">
                            <div className="flo-spinner" />
                        </div>
                    )}

                    {/* End of list */}
                    {!loading && !loadingMore && !hasMore && searchResults === null && displayList.length > 0 && (
                        <div className="flo-end-msg">— end —</div>
                    )}
                </div>
            </div>
        </div>
    );
}
